import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CloseButton from 'components/button/CloseButton';
import { getVisibleSettingsSections, SETTINGS_SECTION_LABEL, SettingsSectionKey } from './sections';
import useAuth from 'auth/useAuth';
import {
  Panel,
  PaneBody,
  PaneHeader,
  PaneTitle,
  SectionList,
  SectionListItem,
  RightPane,
  Background,
} from './SettingsHub.styles';
import { SettingsDetailContainerContext } from './settingsDetailContext';

type Props = {
  section: SettingsSectionKey;
  onSelectSection: (key: SettingsSectionKey) => void;
  onClose: () => void;
  children: ReactNode;
};

export const SettingsHub = ({ section, onSelectSection, onClose, children }: Props) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const { isSuperuser } = useAuth();
  const sections = useMemo(() => getVisibleSettingsSections(isSuperuser), [isSuperuser]);

  const [detailContainer, setDetailContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => previouslyFocused?.focus?.();
  }, []);

  return createPortal(
    <Background onClick={(e) => e.target === e.currentTarget && onClose()}>
      <Panel ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Settings">
        <SectionList aria-label="Settings sections">
          {sections.map(({ key, label, icon: Icon }) => (
            <SectionListItem
              key={key}
              type="button"
              selected={key === section}
              aria-current={key === section ? 'page' : undefined}
              onClick={() => onSelectSection(key)}
            >
              <Icon size={16} />
              <span>{label}</span>
            </SectionListItem>
          ))}
        </SectionList>
        <RightPane ref={setDetailContainer}>
          <PaneHeader>
            <PaneTitle>{SETTINGS_SECTION_LABEL[section]}</PaneTitle>
            <CloseButton onClose={onClose} />
          </PaneHeader>
          <PaneBody>
            <SettingsDetailContainerContext.Provider value={detailContainer}>
              {children}
            </SettingsDetailContainerContext.Provider>
          </PaneBody>
        </RightPane>
      </Panel>
    </Background>,
    document.body,
  );
};

export default SettingsHub;
